import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { CHAT_TOKENS } from '../constants';
import { getFileIcon } from '../utils/fileUtils';

/**
 * `staged` — waiting in the composer tray, on white.
 * `sent`   — already attached to a message, on the grey thread chip.
 */
export type FileChipVariant = 'staged' | 'sent';

export interface FileChipProps {
  fileName: string;
  mimeType?: string;
  variant?: FileChipVariant;
  /** Title attribute — usually "name · type". */
  title?: string;
  hasError?: boolean;
  /** Remove / cancel / download control rendered at the chip's trailing edge. */
  action?: ReactNode;
  /** Second line — file type, upload progress, or a failure note. Staged only. */
  caption?: ReactNode;
}

/**
 * Compact attachment chip.
 *
 * Attachments read as one pill regardless of where they appear, so a document
 * staged in the composer and the same document sent in the thread are visibly
 * the same object — only the surface colour changes.
 */
export const FileChip: FC<FileChipProps> = observer(
  ({ fileName, mimeType, variant = 'staged', title, hasError = false, action, caption }) => {
    const isSent = variant === 'sent';

    return (
      <Box
        title={title ?? fileName}
        sx={{
          display: 'flex',
          alignItems: 'center',
          // A staged document is a decision the user still has to check, so it is
          // sized to be noticed. Once sent it is a reference, and shrinks.
          gap: isSent ? '8px' : '10px',
          maxWidth: isSent ? 260 : 320,
          borderRadius: isSent ? '999px' : '14px',
          backgroundColor: isSent ? CHAT_TOKENS.surface : '#fff',
          border: isSent ? '1px solid transparent' : '1px solid',
          borderColor: isSent ? 'transparent' : hasError ? 'error.main' : '#E3E3E8',
          padding: isSent ? '6px 6px 6px 12px' : '8px 10px 8px 12px',
          boxShadow: isSent ? 'none' : '0 1px 3px rgba(16,24,40,.05)',
          textAlign: 'start',
          '@keyframes fileChipIn': {
            from: { opacity: 0, transform: 'translateY(6px) scale(.94)' },
            to: { opacity: 1, transform: 'none' },
          },
          animation: 'fileChipIn .26s cubic-bezier(.34,1.4,.64,1) both',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            color: hasError ? 'error.main' : 'accent.blue',
          }}
        >
          {getFileIcon(mimeType, isSent ? 18 : 22)}
        </Box>

        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography
            component="span"
            noWrap
            sx={{
              fontSize: isSent ? '0.781rem' : '0.813rem',
              fontWeight: isSent ? 500 : 600,
              lineHeight: 1.35,
              color: hasError ? 'error.main' : isSent ? CHAT_TOKENS.deepInk : 'text.primary',
            }}
          >
            {fileName}
          </Typography>

          {!isSent && caption && (
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: '0.688rem',
                lineHeight: 1.35,
                color: hasError ? 'error.main' : 'text.tertiary',
              }}
            >
              {caption}
            </Typography>
          )}
        </Box>

        {action}
      </Box>
    );
  },
);

/** Round 18px control that sits at a chip's trailing edge. */
export const FileChipAction: FC<{
  onClick?: () => void;
  ariaLabel: string;
  tone?: 'destructive' | 'neutral';
  children: ReactNode;
  disabled?: boolean;
  /** Match the chip it sits in — staged chips are larger. */
  size?: number;
}> = observer(({ onClick, ariaLabel, tone = 'destructive', children, disabled = false, size = 18 }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'transparent',
      color: CHAT_TOKENS.muted,
      cursor: disabled ? 'default' : 'pointer',
      transition: 'background-color .16s ease, color .16s ease, transform .12s ease',
      '&:hover':
        tone === 'destructive'
          ? { backgroundColor: '#FDECEC', color: 'error.main' }
          : { backgroundColor: '#EAEAEA', color: 'text.secondary' },
      '&:active': { transform: 'scale(.85)' },
    }}
  >
    {children}
  </Box>
));

/** Horizontal, wrapping row of chips — the composer tray and the sent-file row. */
export const FileChipRow: FC<{ align?: 'start' | 'end'; children: ReactNode }> = observer(
  ({ align = 'start', children }) => (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
        gap: '6px',
        mb: '7px',
        textAlign: 'start',
      }}
    >
      {children}
    </Box>
  ),
);

/** "+N more" pill shown once the chip list is truncated. */
export const FileChipOverflow: FC<{ count: number }> = observer(({ count }) => {
  const { t } = useTrans();

  return (
    <Typography
      component="span"
      sx={{
        fontSize: '0.719rem',
        color: 'text.secondary',
        backgroundColor: CHAT_TOKENS.surface,
        borderRadius: '999px',
        padding: '6px 10px',
      }}
    >
      {t('chat.attachment.moreCount', { count })}
    </Typography>
  );
});
